import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from './context/AuthContext';

import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import VerifyToken from './pages/VerifyToken';
import SendToken from './pages/SendToken';
import EmailVerification from './pages/EmailVerification';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetCodePage from './pages/ResetCodePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Services from './pages/Services';
import AdminUsersPage from './pages/AdminUsersPage';
import EditServicePage from './pages/EditServicePage';
import ProfilePage from './pages/ProfilePage';
import AdminRequestsPage from './pages/AdminRequestsPage';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import ProviderRegisterPage from "./pages/ProviderRegisterPage";
import ProviderProfilePage from "./pages/ProviderProfilePage";
import AdminProvidersPage from "./pages/AdminProvidersPage";
import ProviderRequestsPage from "./pages/ProviderRequestsPage";
import ProviderQuotesPage from "./pages/ProviderQuotesPage";
import ClientQuotesPage from "./pages/ClientQuotesPage";
import ClientRequestsPage from "./pages/ClientRequestsPage";
import RatingPage from "./pages/RatingPage";
import AdminFormsPage from "./pages/AdminFormsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import ProviderCalendarPage from "./pages/ProviderCalendarPage";
import ProviderPaymentsPage from "./pages/ProviderPaymentsPage";
import { PAGOS_ACTIVOS } from "./config/pagos";
import ChatPage from "./pages/ChatPage";

import { ServiceRequestPage } from './pages/ServiceRequestPage';

// ── Bloque 5 — Páginas legales (rutas públicas) ────────────────────────────
import PaginaEstatica from './components/PaginaEstatica';

// ── Bloque 5 — Modal re-aceptación T&C ────────────────────────────────────
import TermsReacceptModal from './components/TermsReacceptModal';
import SessionExpiredModal from './components/SessionExpiredModal';

// ── Bloque 6 — Módulo financiero ──────────────────────────────────────────
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AdminFinancePage   from './pages/AdminFinancePage';
import AdminDisputasPage  from './pages/AdminDisputasPage';

const AppRoutes = () => (
  <Router>
    <AppRoutesInner />
  </Router>
);

const AppRoutesInner = () => {
  const { user, authReady } = useContext(AuthContext);
  const location = useLocation();

  // La regla vive en el backend: acá solo se lee el campo que calcula.
  const profileGate = user?.perfil_incompleto === true
    && location.pathname !== '/completar-perfil';

  // Esperar a que el AuthContext resuelva la sesión antes de renderizar
  if (!authReady) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f7fa", fontFamily: "Montserrat, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 36, height: 36, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 12px" }} />
        <div style={{ fontSize: 13, color: "#7a93a4", fontWeight: 600 }}>Cargando…</div>
      </div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );

  return (
    <>
      {/* Modal global de re-aceptación — se superpone sobre cualquier página.
          needsTermsReaccept=false → no renderiza nada (ver TermsReacceptModal.jsx) */}
      <TermsReacceptModal />

      {/* Re-login cuando la sesión terminó con el usuario en pantalla: va
          encima de la página sin desmontarla (ver SessionExpiredModal.jsx) */}
      <SessionExpiredModal />

      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/registro" element={<RegisterPage />} />
        <Route path="/completar-perfil" element={<CompleteProfilePage />} />
        <Route path="/verificar" element={<VerifyToken />} />
        <Route path="/solicitar" element={<SendToken />} />
        <Route path="/verificacion" element={<EmailVerification />} />

        {/* Recuperación de contraseña — públicas, sin auth.
            Los pasos 2 y 3 dependen del location.state del anterior; si se
            entra directo, redirigen a /recuperar. */}
        <Route path="/recuperar"        element={<ForgotPasswordPage />} />
        <Route path="/recuperar/codigo" element={<ResetCodePage />} />
        <Route path="/recuperar/clave"  element={<ResetPasswordPage />} />

        {/* Rutas legales — públicas, sin auth requerida */}
        <Route path="/terminos"       element={<PaginaEstatica />} />
        <Route path="/privacidad"     element={<PaginaEstatica />} />
        <Route path="/cookies"        element={<PaginaEstatica />} />
        <Route path="/accesibilidad"  element={<PaginaEstatica />} />
        <Route path="/comunidad"      element={<PaginaEstatica />} />

        {/* Retorno de Mercado Pago — pública (MP redirige sin token) */}
        <Route path="/pago/resultado" element={<PaymentSuccessPage />} />

        {/* Rutas protegidas con layout */}
        <Route element={
          !user
            ? <Navigate to="/" />
            : profileGate
              ? <Navigate to="/completar-perfil" />
              : <Layout />
        }>
          <Route path="/services" element={<PrivateRoute><Services /></PrivateRoute>} />
          <Route path="/admin/users" element={<PrivateRoute requiredRole="admin"><AdminUsersPage /></PrivateRoute>} />
          <Route path="/services/edit/:id" element={<PrivateRoute requiredRole="admin"><EditServicePage /></PrivateRoute>} />
          <Route path="/perfil" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/admin/requests" element={<PrivateRoute requiredRole="admin"><AdminRequestsPage /></PrivateRoute>} />
          <Route path="/solicitar/:serviceKey" element={<PrivateRoute><ServiceRequestPageWrapper /></PrivateRoute>} />

          {/* Panel de solicitudes del cliente */}
          <Route path="/mis-solicitudes" element={<PrivateRoute><ClientRequestsPage /></PrivateRoute>} />
          <Route path="/mis-solicitudes/:requestId/presupuestos" element={<PrivateRoute><ClientQuotesPage /></PrivateRoute>} />

          {/* Calificaciones */}
          <Route path="/calificar/:requestId" element={<PrivateRoute><RatingPage /></PrivateRoute>} />

          {/* Chat — Etapa 8 */}
          <Route path="/chat/:requestId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />

          {/* Módulo prestadores */}
          <Route path="/prestador/registro"     element={<PrivateRoute><ProviderRegisterPage /></PrivateRoute>} />
          <Route path="/prestador/perfil"       element={<PrivateRoute><ProviderProfilePage /></PrivateRoute>} />
          <Route path="/prestador/solicitudes"  element={<PrivateRoute><ProviderRequestsPage /></PrivateRoute>} />
          <Route path="/prestador/presupuestos" element={<PrivateRoute><ProviderQuotesPage /></PrivateRoute>} />
          <Route path="/prestador/agenda"       element={<PrivateRoute><ProviderCalendarPage /></PrivateRoute>} />
          {/* Paso 6 — destino de cobro y payouts. Cierra el link del email
              payoutEjecutado, que ya apuntaba aca y daba 404. */}
          {/* Sin pagos activos (config/pagos.js) la pantalla de cobros no
              existe para el usuario: no tiene link, pero la URL se puede
              escribir a mano. Redirige al panel del prestador. */}
          <Route path="/prestador/pagos"        element={PAGOS_ACTIVOS
            ? <PrivateRoute><ProviderPaymentsPage /></PrivateRoute>
            : <Navigate to="/prestador/solicitudes" replace />} />

          {/* Admin */}
          <Route path="/admin/dashboard"   element={<PrivateRoute requiredRole="admin"><AdminDashboardPage /></PrivateRoute>} />
          <Route path="/admin/prestadores" element={<PrivateRoute requiredRole="admin"><AdminProvidersPage /></PrivateRoute>} />
          <Route path="/admin/formularios" element={<PrivateRoute requiredRole="admin"><AdminFormsPage /></PrivateRoute>} />

          {/* Admin — Bloque 6 */}
          <Route path="/admin/finanzas"   element={<PrivateRoute requiredRole="admin"><AdminFinancePage /></PrivateRoute>} />
          <Route path="/admin/reclamos"  element={<PrivateRoute requiredRole="admin"><AdminDisputasPage /></PrivateRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

function ServiceRequestPageWrapper() {
  const { serviceKey } = useParams();
  return <ServiceRequestPage serviceKey={serviceKey} />;
}

export default AppRoutes;
